/**
 * microsoftAuth.ts (EPR-Service)
 * Microsoft 365 SSO + pending user approval flow.
 * Mirrors OKR Tracker pattern — uses same Firebase project (okr-tracker-staging).
 */
import {
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  linkWithCredential,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const ALLOWED_DOMAINS = ['rascalrepublic.com', 'samaralombok.com'];

const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({
  tenant: import.meta.env.VITE_AZURE_TENANT_ID || 'common',
  prompt: 'select_account',
});
microsoftProvider.addScope('user.read');
microsoftProvider.addScope('openid');
microsoftProvider.addScope('email');
microsoftProvider.addScope('profile');

export const isAllowedDomain = (email: string): boolean => {
  const domain = email.split('@')[1]?.toLowerCase();
  return ALLOWED_DOMAINS.includes(domain);
};

export const signInWithMicrosoft = async (existingPassword?: string): Promise<FirebaseUser> => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    const user = result.user;

    if (user.email && !isAllowedDomain(user.email)) {
      await auth.signOut();
      throw new Error(
        'Access restricted to company email addresses only. Please use your @rascalrepublic.com or @samaralombok.com account.'
      );
    }

    return user;
  } catch (error: any) {
    if (error.code === 'auth/account-exists-with-different-credential') {
      const credential = OAuthProvider.credentialFromError(error);
      const email = error.customData?.email as string | undefined;

      if (!credential || !email) {
        throw new Error('Account linking failed. Please sign in with email/password instead.');
      }

      if (existingPassword) {
        const emailResult = await signInWithEmailAndPassword(auth, email, existingPassword);
        await linkWithCredential(emailResult.user, credential);
        return emailResult.user;
      }

      // Signal to UI that linking dialog is needed
      const linkErr = new Error('ACCOUNT_LINK_REQUIRED');
      (linkErr as any).email = email;
      (linkErr as any).credential = credential;
      throw linkErr;
    }

    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled. The popup was closed.');
    }
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up blocked by browser. Please allow pop-ups for this site and try again.');
    }
    if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Sign-in cancelled.');
    }
    // Re-throw already-formatted errors (e.g. domain check above)
    if (error.message && !error.code) throw error;

    throw new Error('Microsoft sign-in failed. Please try again.');
  }
};

export const linkMicrosoftAccount = async (
  email: string,
  password: string,
  credential: any
): Promise<FirebaseUser> => {
  try {
    const emailResult = await signInWithEmailAndPassword(auth, email, password);
    await linkWithCredential(emailResult.user, credential);
    return emailResult.user;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error('Incorrect password. Please try again.');
    }
    throw new Error('Account linking failed: ' + (error.message || 'Unknown error'));
  }
};

// ── Pending User Types ────────────────────────────────────────

export interface PendingUserRecord {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  requestedAt: any;
  status: 'pending' | 'approved' | 'rejected';
  appId?: string;
  authProvider?: string;
  rejectionReason?: string;
  approvedBy?: string;
  rejectedBy?: string;
}

// ── Pending User CRUD ─────────────────────────────────────────

export const createPendingUser = async (
  uid: string,
  email: string,
  displayName: string,
  photoURL?: string
): Promise<{ status: 'created' | 'pending' | 'rejected' | 'approved' }> => {
  const pendingRef = doc(db, 'pendingUsers', uid);
  const existing = await getDoc(pendingRef);

  if (existing.exists()) {
    const existingStatus = existing.data()?.status as string;
    if (existingStatus === 'approved') {
      // Was approved before but Firestore user doc was removed — re-request
      await deleteDoc(pendingRef);
      await setDoc(pendingRef, {
        uid,
        email,
        displayName: displayName || email.split('@')[0],
        photoURL: photoURL ?? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || email)}`,
        requestedAt: serverTimestamp(),
        status: 'pending',
        appId: 'epr',
        authProvider: 'microsoft.com',
        note: 'Re-requested after account was removed',
      });
      return { status: 'created' };
    }
    return { status: existingStatus as 'pending' | 'rejected' };
  }

  await setDoc(pendingRef, {
    uid,
    email,
    displayName: displayName || email.split('@')[0],
    photoURL: photoURL ?? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || email)}`,
    requestedAt: serverTimestamp(),
    status: 'pending',
    appId: 'epr',
    authProvider: 'microsoft.com',
  });

  return { status: 'created' };
};

export const approvePendingUser = async (
  uid: string,
  role: 'employee' | 'leader' | 'hr_admin',
  approvedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const pendingRef = doc(db, 'pendingUsers', uid);
    const pendingDoc = await getDoc(pendingRef);
    if (!pendingDoc.exists()) return { success: false, error: 'Pending user not found' };

    const pd = pendingDoc.data();

    await setDoc(
      doc(db, 'users', uid),
      {
        uid,
        id: uid,
        email: pd.email,
        name: pd.displayName,
        displayName: pd.displayName,
        username: (pd.email as string).split('@')[0].toLowerCase(),
        photoURL: pd.photoURL,
        avatar: pd.photoURL,
        role,
        isLeader: role === 'leader',
        isActive: true,
        appAccess: ['epr'],
        authProvider: 'microsoft.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        approvedBy,
        approvedAt: new Date().toISOString(),
      },
      { merge: true }
    );

    await setDoc(
      pendingRef,
      { status: 'approved', approvedBy, approvedAt: serverTimestamp(), assignedRole: role },
      { merge: true }
    );

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message ?? 'Failed to approve user' };
  }
};

export const rejectPendingUser = async (
  uid: string,
  rejectedBy: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await setDoc(
      doc(db, 'pendingUsers', uid),
      {
        status: 'rejected',
        rejectedBy,
        rejectedAt: serverTimestamp(),
        rejectionReason: reason ?? '',
      },
      { merge: true }
    );
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message ?? 'Failed to reject user' };
  }
};

export const getPendingUsers = async (): Promise<PendingUserRecord[]> => {
  const snap = await getDocs(
    query(
      collection(db, 'pendingUsers'),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    )
  );
  return snap.docs.map((d) => ({ uid: d.id, ...d.data() }) as PendingUserRecord);
};
